/**
 * Transport cluster: play/pause, fixed-size jumps, playback speed.
 *
 * The native <video controls> bar has play/pause and a scrub bar but no
 * fixed-size jumps — rewatching a moment means dragging the scrubber and
 * overshooting. These sit just above the native bar with the same
 * visibility rule as the marks strip: video chrome shows on hover and
 * pins while paused, so nothing floats over the film mid-playback.
 *
 * MOVABLE: the grip on the left drags the cluster anywhere inside the
 * video container (it can cover a scoreboard or sit clear of the action).
 * The spot persists per-browser in localStorage as PERCENTAGES of the
 * container, so a resized or different-sized player keeps the same
 * relative spot; double-click the grip to snap back to the default.
 *
 * Deliberately operates on the <video> element directly (like the arrow
 * keys and marks chips do) rather than lifting playback into React state —
 * the element is the source of truth for time, and the page's own comment
 * on onFrameTick explains why re-rendering on it is off the table.
 */
import { useRef, useState } from 'react'

// The cluster's glyphs — BOTH weights it uses: the skips are regular,
// play/pause is 'ph-fill' (line ~199). Imported HERE, not left to the
// page: the match page loads these anyway, but the Workshop dashboard
// and segment workbench reuse this component and shipped buttons with
// empty boxes where play/skip should be — and then a visible skip row
// around an invisible play button when only regular was imported
// (Ben, 2026-10-15, twice).
import '@phosphor-icons/web/regular'
import '@phosphor-icons/web/fill'

/** Cycle order for the speed button. 1 first so the initial click is the
 *  most common ask (slightly faster review); 0.5 last, next to 1. */
const RATES = [1, 1.25, 1.5, 2, 0.5] as const

/** localStorage key for the dragged position (same per-user-preference
 *  mechanism as tel_controls_visible). */
const POS_KEY = 'video_transport_pos'

type Pos = { x: number; y: number }

/** Clamp a persisted or in-flight position so the cluster can never be
 *  parked out of reach — garbage in storage must not hide the controls. */
function clampPos(p: Pos): Pos {
  return {
    x: Math.min(Math.max(p.x, 0), 90),
    y: Math.min(Math.max(p.y, 0), 90),
  }
}

function loadPos(): Pos | null {
  try {
    const raw = localStorage.getItem(POS_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as Pos
    if (typeof p.x !== 'number' || typeof p.y !== 'number') return null
    return clampPos(p)
  } catch {
    return null
  }
}

export function TransportControls({ getVideo, paused }: {
  /** The player's <video>, read at click time — never stale, never held. */
  getVideo: () => HTMLVideoElement | null
  /** Mirrored element state from the player (its play/pause listeners). */
  paused: boolean
}) {
  const [rate, setRate] = useState<number>(1)
  /** null = the default CSS spot (bottom-centre, above the native bar). */
  const [pos, setPos] = useState<Pos | null>(loadPos)
  const [dragging, setDragging] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  /** Pointer offset inside the cluster at grab time, so the cluster does
   *  not jump so its corner meets the pointer. */
  const grabRef = useRef({ dx: 0, dy: 0 })

  const seekBy = (delta: number) => {
    const v = getVideo()
    if (!v || !v.src) return
    // duration is NaN before metadata arrives; Math.min(NaN, …) is NaN and
    // would silently no-op the jump.
    const dur = Number.isFinite(v.duration) ? v.duration : Infinity
    v.currentTime = Math.min(Math.max(v.currentTime + delta, 0), dur)
  }

  const toggle = () => {
    const v = getVideo()
    if (!v || !v.src) return
    if (v.paused) void v.play()
    else v.pause()
  }

  const cycleRate = () => {
    const v = getVideo()
    if (!v) return
    const next = RATES[(RATES.indexOf(rate as typeof RATES[number]) + 1) % RATES.length]!
    v.playbackRate = next
    setRate(next)
  }

  // ── drag, via pointer capture on the grip ────────────────────────────
  // Capture keeps move/up events coming even when the pointer leaves the
  // container mid-drag. Position is computed against the offsetParent
  // (.video-container is position:relative) and stored as percentages.

  const onGripDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    const root = rootRef.current
    if (!root) return
    const rect = root.getBoundingClientRect()
    grabRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    setDragging(true)
    // Capture keeps the drag alive off-element. jsdom implements this but
    // THROWS for a pointer id it never saw go active — never let that
    // undo the grab (it is an enhancement, not a precondition).
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* fine */ }
  }

  const onGripMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging) return
    const root = rootRef.current
    const box = root?.parentElement // the .video-container
    if (!root || !box) return
    const boxRect = box.getBoundingClientRect()
    const rect = root.getBoundingClientRect()
    if (boxRect.width === 0 || boxRect.height === 0) return
    const left = e.clientX - grabRef.current.dx - boxRect.left
    const top = e.clientY - grabRef.current.dy - boxRect.top
    // Keep the whole cluster inside the container.
    const maxLeft = Math.max(boxRect.width - rect.width, 0)
    const maxTop = Math.max(boxRect.height - rect.height, 0)
    setPos(clampPos({
      x: (Math.min(Math.max(left, 0), maxLeft) / boxRect.width) * 100,
      y: (Math.min(Math.max(top, 0), maxTop) / boxRect.height) * 100,
    }))
  }

  const onGripUp = () => {
    if (!dragging) return
    setDragging(false)
    setPos((p) => {
      try {
        if (p) localStorage.setItem(POS_KEY, JSON.stringify(p))
      } catch { /* private mode: the spot just does not persist */ }
      return p
    })
  }

  const resetPos = () => {
    setPos(null)
    try { localStorage.removeItem(POS_KEY) } catch { /* ditto */ }
  }

  const jump = (secs: number, dir: -1 | 1) => (
    <button
      type="button"
      aria-label={`${dir < 0 ? 'Back' : 'Forward'} ${secs} seconds`}
      title={`${dir < 0 ? 'Back' : 'Forward'} ${secs}s`
        + (secs === 5 ? ' (Shift+arrow)' : '')}
      onClick={() => seekBy(dir * secs)}
    >
      <i className={`ph ph-arrow-${dir < 0 ? 'counter-clockwise' : 'clockwise'}`}
         aria-hidden="true" />
      {secs}
    </button>
  )

  return (
    <div
      ref={rootRef}
      className={'video-transport'
        + (paused ? ' video-transport--pinned' : '')
        + (dragging ? ' video-transport--dragging' : '')}
      // Inline position overrides the stylesheet's centred default only
      // when a spot has been chosen; transform/bottom must be neutralised
      // or the percentages would fight the translateX(-50%).
      style={pos ? { left: `${pos.x}%`, top: `${pos.y}%`,
                     bottom: 'auto', transform: 'none' } : undefined}
    >
      <button
        type="button"
        className="video-transport__grip"
        aria-label="Move controls"
        title="Drag to move · double-click to reset"
        onPointerDown={onGripDown}
        onPointerMove={onGripMove}
        onPointerUp={onGripUp}
        onDoubleClick={resetPos}
      >
        <i className="ph ph-dots-six-vertical" aria-hidden="true" />
      </button>
      {jump(30, -1)}
      {jump(5, -1)}
      <button
        type="button"
        aria-label={paused ? 'Play' : 'Pause'}
        title={`${paused ? 'Play' : 'Pause'} (Space)`}
        onClick={toggle}
      >
        {/* Fill weight is 'ph-fill ph-play' — 'ph ph-play-fill' is not a
            glyph and rendered an invisible button (caught live, 2026-09-24). */}
        <i className={`ph-fill ph-${paused ? 'play' : 'pause'}`} aria-hidden="true" />
      </button>
      {jump(5, 1)}
      {jump(30, 1)}
      <button
        type="button"
        aria-label="Playback speed"
        title="Playback speed"
        onClick={cycleRate}
      >
        {rate}×
      </button>
    </div>
  )
}
